import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import StarEmpty from '../../design-system/icons/core/star-empty.svg';
import StarFilled from '../../design-system/icons/core/star-filled.svg';
import StarHalf from '../../design-system/icons/core/star-half.svg';
import RCIcon from './rc_icon';
import Tooltip from './tooltip';

interface IStarRatingProps {
  rating: number; // decimal rating from 0.0 to 1.0
  count?: number;
  largeStars?: boolean;
  hideTooltip?: boolean;
}

function getStarsCount(rating) {
  const ratingOutOf5 = Math.max(0, Math.min(rating, 1.0)) * 5.0;
  const roundedToTheNearestHalf = Math.round(ratingOutOf5 / 0.5) * 0.5;

  const fullStars = Math.floor(roundedToTheNearestHalf);
  const halfStars = roundedToTheNearestHalf > fullStars ? 1 : 0;
  const emptyStars = 5 - fullStars - halfStars;

  return {
    fullStars,
    halfStars,
    emptyStars,
    displayRating: ratingOutOf5.toLocaleString(undefined, { maximumFractionDigits: 2 }),
  };
}

export default function StarRating({
  rating,
  count,
  largeStars,
  hideTooltip,
}: IStarRatingProps) {
  const classes = classNames(
    'star-rating',
    { 'star-rating--large': largeStars },
  );

  const {
    fullStars,
    halfStars,
    emptyStars,
    displayRating,
  } = getStarsCount(rating);

  if (count === 0) return null;

  const ratingText = I18n.t('commons.starRating.ratingText', { rating: displayRating });

  const starsContainer = (
    <div className="star-rating__stars" aria-label={ratingText} role="img">
      {[...Array(fullStars)].map((_, index) => (
        <RCIcon key={index} svgComponent={StarFilled} />
      ))}
      {!!halfStars && (
        <RCIcon key={fullStars} svgComponent={StarHalf} />
      )}
      {[...Array(emptyStars)].map((_, index) => (
        <RCIcon key={fullStars + 1 + index} svgComponent={StarEmpty} />
      ))}
    </div>
  );

  return (
    <div className={classes}>
      {hideTooltip && starsContainer}
      {!hideTooltip && (
        <Tooltip text={ratingText} type="plain">
          {starsContainer}
        </Tooltip>
      )}

      {count !== undefined &&
        <div className="star-rating__count">
          {`(${Math.round(count).toLocaleString()})`}
        </div>
      }
    </div>
  );
}
